import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';

import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import FormGroup from '@mui/material/FormGroup';
import Tooltip from '@mui/material/Tooltip';
import Switch from '@mui/material/Switch';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import FilePresent from '@mui/icons-material/FilePresent';

import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { useDataAuth } from '../../hooks/useDataAuth';

import { JsonTree } from '../jsonEditorV2/jsontree/JsonTree';
import { HtmlEditor } from '../htmlEditor/HtmlEditor';
import { ArrayTextEditor } from '../arrayTextEditor/ArrayTextEditor';
import { MultiLanguageEditor } from '../others/MultiLanguageEditor';
import { GenericAutocomplete } from '../others/GenericAutocomplete';
import { MultipleSelect } from './MultipleSelect';
import HelpIconPopover from '../others/HelpIconPopover';
import { MultiFileUpload } from '../MultiFileUpload/MultiFileUpload';
import PhotoCapture from './PhotoCapture';

import { ensureArray } from '../../utils/validateResponseApi';
import { isNotEmptyObject, safeParseJSON, safeStringifyJSON } from '../../utils/jsonValidator';
import { evaluateCondition } from '../../utils/evaluateCondition';
import { getNestedValue } from '../../utils/objectHandlers';
import { DynamicSelect } from './DynamicSelect';

const DynamicForm = ({
    values,
    scheme,
    errorInputs,
    onChange,
    handleFileChange,
    handleUploadClick,
    setChanges,
    setValuesSelectGeneric = null,
    companyInfo = {},
    gridConfig = { columns: 1 }
}) => {
    const [{ userSession }] = useDataAuth();
    const [formData, setFormData] = useState(values);
    const [optionsByField, setOptionsByField] = useState({});
    const [dependencyMap, setDependencyMap] = useState({});

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
        onChange(e);
    };

    useEffect(() => {
        const map = {};
        scheme.forEach(field => {
            if (field.trigger_dependency && Array.isArray(field.trigger_dependency)) {
                field.trigger_dependency.forEach(childId => {
                    if (!map[field.id]) map[field.id] = [];
                    if (!map[field.id].includes(childId)) map[field.id].push(childId);
                });
            }
            if (field.dependsOn) {
                if (!map[field.dependsOn]) map[field.dependsOn] = [];
                if (!map[field.dependsOn].includes(field.id)) {
                    map[field.dependsOn].push(field.id);
                }
            }
        });
        setDependencyMap(map);
    }, [scheme]);

    useEffect(() => {
        setFormData(values);
    }, [values])

    const handleSubmit = (e) => e.preventDefault();

    const handleChanges = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        console.log({ name, value });
        setChanges(name, value);
    };

    // useEffect(() => {
    //     const fetchOptions = async () => {
    //         const selectFields = scheme.filter((field) => field.type === 'select_trigger' && field.trigger && !field.dependsOn);
    //         const optionsEntries = await Promise.all(
    //             selectFields.map(async (field) => {
    //                 const { url, method, bodyData } = field.trigger[0];
    //                 try {
    //                     if (isNotEmptyObject(companyInfo)) {
    //                         let varb = safeParseJSON(bodyData?.variables);
    //                         for (let key in companyInfo) {
    //                             varb[key] = companyInfo[key];
    //                         }
    //                         bodyData.variables = safeStringifyJSON(varb, 0);
    //                     }
    //                     const response = await axios({
    //                         method,
    //                         url,
    //                         headers: {
    //                             'Content-Type': 'application/json',
    //                             'Authorization': `Bearer ${userSession.token}`,
    //                         },
    //                         data: bodyData,
    //                     });
    //                     const options = response.data || [];
    //                     const val = options[field.id]?.find(o => o.defaultValue)?.code || '';
    //                     if (val !== '') handleChanges(field.id, val);
    //                     return [field.id, options];
    //                 } catch (error) {
    //                     console.error(`Error fetching options for ${field.id}`, error);
    //                     return [field.id, []];
    //                 }
    //             })
    //         );
    //         setOptionsByField(Object.fromEntries(optionsEntries));
    //     };
    //     fetchOptions();
    // }, [scheme, userSession.token]);

    // const fetchFieldOptions = useCallback(async (field) => {
    //     if (!field.trigger?.[0]) return;
    //     const trigger = field.trigger[0];
    //     let bodyData = { ...trigger.bodyData };
    //     Object.keys(bodyData).forEach(key => {
    //         if (typeof bodyData[key] === 'string') {
    //             bodyData[key] = bodyData[key].replace(/\{(\w+)\}/g, (_, fieldName) => {
    //                 return formData[fieldName] ?? '';
    //             });
    //         }
    //     });
    //     try {
    //         const response = await axios({
    //             method: trigger.method,
    //             url: trigger.url,
    //             headers: {
    //                 'Content-Type': 'application/json',
    //                 'Authorization': `Bearer ${userSession.token}`,
    //             },
    //             data: bodyData,
    //         });
    //         const options = response.data || [];
    //         setOptionsByField(prev => ({ ...prev, [field.id]: options }));
    //     } catch (error) {
    //         console.error(`Error fetching ${field.id}:`, error);
    //         setOptionsByField(prev => ({ ...prev, [field.id]: [] }));
    //     }
    // }, [formData, userSession.token]);

    // useEffect(() => {
    //     const dependentFields = scheme.filter(field => field.type === 'select_trigger' && field.dependsOn);
    //     dependentFields.forEach(field => {
    //         const parentValue = formData[field.dependsOn];
    //         if (parentValue) {
    //             fetchFieldOptions(field);
    //         } else {
    //             setOptionsByField(prev => ({ ...prev, [field.id]: [] }));
    //         }
    //     });
    // }, [formData]);

    //#region Parse data Select
    function parseSelectOptions(options) {
        if (!Array.isArray(options)) return [];
        return options.map(option => {
            if (typeof option === 'string')
                return { name: option, code: option };
            else if (typeof option === 'object' && option?.key && option?.value)
                return { name: option?.key, code: option?.value };
            return null;
        }).filter(Boolean);
    }

    // const handleSelectChange = (name, value) => {
    //     handleChanges(name, value);
    //     if (dependencyMap[name]) {
    //         dependencyMap[name].forEach(childId => {
    //             setFormData(prev => ({ ...prev, [childId]: '' }));
    //             setChanges(childId, '');
    //             setOptionsByField(prev => ({ ...prev, [childId]: [] }));
    //         });
    //     }
    // };

    useEffect(() => {
        const switches = {};
        scheme.forEach(field => {
            if (field.type === 'switch') {
                switches[field.id] = false;
            }
        });
        setFormData(prev => ({
            ...switches,
            ...prev
        }));
    }, [scheme]);

    const cols = gridConfig?.columns || 1;
    const gridSize = cols > 1 ? Math.floor(12 / cols) : 12;

    const renderField = (field, mtop = 0) => {
        const isDependent = !!field.dependsOn;
        const parentValue = field.dependsOn ? formData[field.dependsOn] : true;
        const disabled = isDependent && (parentValue === '' || parentValue === null || parentValue === undefined);
        return (
            <Box
                key={field.id}
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="center"
                sx={{ width: '100%', position: 'relative' }}
            >
                <Box sx={{ width: '15px', display: { xs: 'none', sm: 'block' } }} />
                <FormGroup
                    sx={{
                        width: '100%',
                        mt: mtop
                    }}
                >
                    {
                        field.type === 'switch' ? (
                            <FormControl
                                required={field.required}
                                error={Boolean(errorInputs[field.id]?.isError)}
                            >
                                <FormControlLabel
                                    control={
                                        <Switch
                                            id={field.id}
                                            name={field.id}
                                            checked={Boolean(formData[field.id])}
                                            required={field.required}
                                            onChange={handleChange}
                                        />
                                    }
                                    label={field.label}
                                />
                                {errorInputs[field.id]?.isError && (
                                    <FormHelperText>{errorInputs[field.id]?.message}</FormHelperText>
                                )}
                            </FormControl>
                        ) : field.type === 'date' ? (
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    label={field.label}
                                    value={formData[field.id] ? moment(formData[field.id], 'YYYY/MM/DD') : null}
                                    onChange={(newValue) => {
                                        handleChange({
                                            target: {
                                                name: field.id,
                                                // Verificamos si newValue es válido antes de formatear
                                                value: newValue && newValue.isValid() ? newValue.format('YYYY/MM/DD') : null
                                            }
                                        });
                                    }}
                                    slotProps={{
                                        textField: {
                                            id: field.id,
                                            name: field.id,
                                            size: "small",
                                            fullWidth: true,
                                            required: field.required,
                                            error: Boolean(errorInputs[field.id]?.isError),
                                            helperText: errorInputs[field.id]?.messageError,
                                            InputLabelProps: { shrink: true }
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                        ) : field.type === 'key_value' ? (
                            <JsonTree
                                keyParent={field.id}
                                contentJson={!formData[field.id] ? {} : formData[field.id]}
                                handleChange={handleChanges}
                            />
                        ) : field.type === 'upload' ? (
                            <FormGroup
                                style={{ height: '30px', display: 'flex' }}
                            >
                                <TextField
                                    id={field.id}
                                    name={field.id}
                                    size="small"
                                    fullWidth
                                    type="text"
                                    label={field.label}
                                    value={formData[field.id] || ''}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ readOnly: true }}
                                    error={Boolean(errorInputs[field.id]?.isError)}
                                    helperText={errorInputs[field.id]?.messageError}
                                    InputProps={{
                                        endAdornment: (
                                            <>
                                                <label htmlFor={`file-upload-${field.id}`}>
                                                    <Tooltip title={'Seleccionar archivo'}>
                                                        <FilePresent style={{ fontSize: 25, color: 'gray' }} />
                                                    </Tooltip>
                                                </label>
                                            </>
                                        ),
                                    }}
                                />
                                <input
                                    accept=".pdf, .xml, .eml, .zip, .jpg, .png"
                                    style={{ display: 'none' }}
                                    id={`file-upload-${field.id}`}
                                    type="file"
                                    onChange={(e) => handleFileChange(e, field.id)}
                                />
                            </FormGroup >
                        ) : field.type === 'multi_file_upload' ? (
                            <MultiFileUpload
                                config={field}
                                files={Array.isArray(formData[field.id]) ? formData[field.id] : []}
                                onChange={(files) => handleChanges(field.id, files)}
                            />
                        ) : field.type === 'html' ? (
                            <HtmlEditor
                                field={field}
                                html={formData[field.id] || ''}
                                handleChange={handleChanges}
                            />
                        ) : field.type === 'textArray' ? (
                            <ArrayTextEditor
                                field={field}
                                arrayText={formData[field.id] || []}
                                handleChange={handleChanges}
                            />
                        ) : field.type === 'MLEditor' ? (
                            <MultiLanguageEditor
                                value={formData[field.id] || ''}
                                onChange={(e) => handleChanges(field.id, e)}
                                dfLanguage={field?.hasOwnProperty('subtype') ? field.subtype : 'plaintext'}
                                htCtrl={field?.hasOwnProperty('height') ? field.height : '250'}
                                errorInputs={errorInputs}
                                field={field}
                            />
                        ) : field.type === 'select' ? (
                            <GenericAutocomplete
                                options={ensureArray(parseSelectOptions(field.values))}
                                value={formData[field.id] ? formData[field.id] : ''}
                                label={field.label}
                                name={field.id}
                                onChange={handleChange}
                                error={Boolean(errorInputs[field.id]?.isError)}
                                helperText={errorInputs[field.id]?.messageError}
                                required={field?.required}
                                disableCloseOnSelect={false}
                            />
                        ) : field.type === 'select_config' ? (
                            <GenericAutocomplete
                                options={ensureArray(getNestedValue(companyInfo, field.path))}
                                value={formData[field.id] ? formData[field.id] : ''}
                                label={field.label}
                                name={field.id}
                                onChange={setValuesSelectGeneric}
                                error={Boolean(errorInputs[field.id]?.isError)}
                                helperText={errorInputs[field.id]?.messageError}
                                required={field?.required}
                                disableCloseOnSelect={false}
                            />
                        ) : field.type === 'select_trigger' ? (
                            <DynamicSelect
                                field={field}
                                companyInfo={companyInfo}
                                formData={formData}
                                errorInputs={errorInputs}
                                onChange={setValuesSelectGeneric}
                                handleChanges={handleChanges}
                                disabled={disabled}
                            />
                        ) : field.type === 'multiple_select' ? (
                            <MultipleSelect
                                field={field}
                                companyInfo={companyInfo}
                                values={ensureArray(formData[field.id])}
                                errorInputs={errorInputs}
                                handleChange={(e) => handleChanges(field.id, e.target.value)}
                            />
                        ) : field.type === 'photo_capture' ? (
                            <PhotoCapture
                                field={field}
                                value={formData[field.id] || ''}
                                onChange={handleChanges}
                                error={Boolean(errorInputs[field.id]?.isError)}
                                helperText={errorInputs[field.id]?.messageError}
                                required={field?.required}
                            />
                        ) : (
                            <TextField
                                id={field.id}
                                name={field.id}
                                size="small"
                                fullWidth
                                type={field.type}
                                label={field.label}
                                required={field.required}
                                value={formData[field.id] || ''}
                                InputLabelProps={{ shrink: true }}
                                onChange={handleChange}
                                error={Boolean(errorInputs[field.id]?.isError)}
                                helperText={errorInputs[field.id]?.messageError}
                            />
                        )
                    }
                </FormGroup >

                <Box
                    sx={{
                        width: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        pl: '2px'
                    }}
                >
                    {
                        field.help &&
                        (
                            <HelpIconPopover htmlContent={field.help} />
                        )}
                </Box>
            </Box >
        );
    };

    return (
        <form onSubmit={handleSubmit}>
            {cols === 1 ? (
                scheme
                    .filter(field => evaluateCondition(field.visible))
                    .map(field => renderField(field, 1.5))
            ) : (
                <Grid container spacing={2} sx={{ mt: 2 }}>
                    {scheme
                        .filter(field => evaluateCondition(field.visible))
                        .map((field) => (
                            <Grid size={{ xs: 12, sm: 6, md: gridSize }} key={field.id}>
                                {renderField(field)}
                            </Grid>
                        ))
                    }
                </Grid>
            )}
        </form>
    );
};
export default DynamicForm;